// One file's unified diff, as `git diff` writes it, read into the rows the
// Changes pane draws: each line with the number it has in the old file, in
// the new one, or in both.
//
// A hunk's header says how many lines of each file it holds, and the lines
// are told apart by those counts, not by what they look like: a removed line
// that reads `-- a/x` is written `--- a/x`, which only its place in a hunk
// tells from a file's header.

export type DiffRow =
  /** A hunk's header: `@@ -12,7 +12,9 @@ function name()`. */
  | { kind: 'hunk'; text: string }
  /** A line both files have. */
  | { kind: 'context'; old: number; new: number; text: string }
  | { kind: 'add'; new: number; text: string }
  | { kind: 'del'; old: number; text: string }
  /** What Git says beside the lines: `\ No newline at end of file`, a rename, a change of mode. */
  | { kind: 'note'; text: string }

export interface ParsedDiff {
  rows: DiffRow[]
  /** Git says the file is binary: there are no lines to show. */
  binary: boolean
  additions: number
  deletions: number
}

const HUNK = /^@@ -(\d+)(?:,(\d+))? \+(\d+)(?:,(\d+))? @@/
const BINARY = /^(?:Binary files .* differ|GIT binary patch)$/
/** What a diff says of a file before its lines that the lines do not show: another name, another mode. */
const SAID_OF_THE_FILE = /^(?:old mode|new mode|rename from|rename to|copy from|copy to|similarity index)\b/
/** And what is worth saying only when no line follows: an empty file that is new, or gone. */
const SAID_WHEN_ALONE = /^(?:new file mode|deleted file mode)\b/

export function parseDiff(text: string): ParsedDiff {
  const lines = text.split('\n')
  // The text ends with its last line's newline: nothing follows it.
  if (lines[lines.length - 1] === '') lines.pop()

  const rows: DiffRow[] = []
  const alone: string[] = []
  let binary = false
  let additions = 0
  let deletions = 0
  /** Inside a hunk: the next line's number in each file, and the lines of each the hunk still holds. */
  let hunk: { old: number; new: number; oldLeft: number; newLeft: number } | null = null

  for (const raw of lines) {
    // A file with CRLF line ends keeps its CR in every line: it is no part of what the line says.
    const line = raw.endsWith('\r') ? raw.slice(0, -1) : raw
    if (hunk && (hunk.oldLeft > 0 || hunk.newLeft > 0)) {
      const sign = line[0]
      const rest = line.slice(1)
      if (sign === '+') {
        rows.push({ kind: 'add', new: hunk.new++, text: rest })
        hunk.newLeft -= 1
        additions += 1
        continue
      }
      if (sign === '-') {
        rows.push({ kind: 'del', old: hunk.old++, text: rest })
        hunk.oldLeft -= 1
        deletions += 1
        continue
      }
      // A blank context line has lost its space to some tool on the way.
      if (sign === ' ' || line === '') {
        rows.push({ kind: 'context', old: hunk.old++, new: hunk.new++, text: rest })
        hunk.oldLeft -= 1
        hunk.newLeft -= 1
        continue
      }
      if (sign === '\\') {
        rows.push({ kind: 'note', text: rest.trim() })
        continue
      }
      // Not a line of this hunk after all: the diff was cut, or is not what its header said.
      hunk = null
    }
    const header = HUNK.exec(line)
    if (header) {
      hunk = {
        old: Number(header[1]),
        new: Number(header[3]),
        oldLeft: header[2] === undefined ? 1 : Number(header[2]),
        newLeft: header[4] === undefined ? 1 : Number(header[4]),
      }
      rows.push({ kind: 'hunk', text: line })
      continue
    }
    if (line.startsWith('\\')) {
      // After the last line of a hunk: it speaks of that line.
      rows.push({ kind: 'note', text: line.slice(1).trim() })
      continue
    }
    if (BINARY.test(line)) binary = true
    else if (SAID_OF_THE_FILE.test(line)) rows.push({ kind: 'note', text: line })
    else if (SAID_WHEN_ALONE.test(line)) alone.push(line)
  }

  if (binary) return { rows: [], binary, additions, deletions }
  if (!rows.some((row) => row.kind === 'hunk')) rows.unshift(...alone.map((text): DiffRow => ({ kind: 'note', text })))
  return { rows, binary, additions, deletions }
}
